import type { ChatSubagentCard } from "../ui-types.ts";
import {
  readChatMessagesForSession,
  readPendingUserMessageIdsForSession,
  writeChatMessagesForSession,
  writePendingUserMessageIdsForSession,
  type ChatMessagesBySessionCache,
  type PendingUserMessageIdsBySessionCache,
} from "./chat-messages-by-session.ts";
export interface ChatSessionCacheSnapshot {
  sessionKey: string;
  messages: unknown[];
  pendingUserMessageIds: string[];
  subagentCards: ChatSubagentCard[];
  chatRunId: string | null;
  chatSending: boolean;
}

/**
 * Authoritative per-session chat state for the Control UI.
 * Tab switches stash/restore through this store so sends pinned to an
 * originating session never paint onto the wrong tab.
 */
export class ChatSessionStore {
  readonly messagesBySession: ChatMessagesBySessionCache = new Map();
  readonly pendingIdsBySession: PendingUserMessageIdsBySessionCache = new Map();

  readMessages(sessionKey: string): unknown[] {
    return readChatMessagesForSession(this.messagesBySession, sessionKey) as unknown[];
  }

  writeMessages(sessionKey: string, messages: readonly unknown[]): void {
    writeChatMessagesForSession(this.messagesBySession, sessionKey, messages);
  }

  readPendingIds(sessionKey: string): string[] {
    return readPendingUserMessageIdsForSession(this.pendingIdsBySession, sessionKey);
  }

  writePendingIds(sessionKey: string, ids: readonly string[]): void {
    writePendingUserMessageIdsForSession(this.pendingIdsBySession, sessionKey, ids);
  }

  /** Stash the active session before switching away. */
  stashActiveSession(params: {
    sessionKey: string;
    messages: readonly unknown[];
    pendingUserMessageIds: readonly string[];
  }): void {
    const key = params.sessionKey.trim();
    if (!key) {
      return;
    }
    this.writeMessages(key, params.messages);
    this.writePendingIds(key, params.pendingUserMessageIds);
  }

  /** Restore cached rows when activating a session (may be empty). */
  activateSession(sessionKey: string): { messages: unknown[]; pendingUserMessageIds: string[] } {
    const key = sessionKey.trim();
    return {
      messages: this.readMessages(key),
      pendingUserMessageIds: this.readPendingIds(key),
    };
  }

  snapshot(params: {
    sessionKey: string;
    activeMessages: readonly unknown[];
    activePendingIds: readonly string[];
    subagentCards: ChatSubagentCard[];
    chatRunId: string | null;
    chatSending: boolean;
  }): ChatSessionCacheSnapshot {
    const key = params.sessionKey.trim();
    if (!key) {
      return {
        sessionKey: "",
        messages: [],
        pendingUserMessageIds: [],
        subagentCards: [],
        chatRunId: null,
        chatSending: false,
      };
    }
    return {
      sessionKey: key,
      messages: [...params.activeMessages],
      pendingUserMessageIds: [...params.activePendingIds],
      subagentCards: [...params.subagentCards],
      chatRunId: params.chatRunId,
      chatSending: params.chatSending,
    };
  }

  clear(): void {
    this.messagesBySession.clear();
    this.pendingIdsBySession.clear();
  }
}
